import { useState, useEffect } from 'react';
import { useInventory } from '../hooks/useInventoryOperations';
import { useNavigate } from 'react-router-dom';

const InventorySummaryPage = () => {
  const navigate = useNavigate();
  const { getSummary, getValueReport, loading } = useInventory();
  const [summary, setSummary] = useState([]);
  const [total, setTotal] = useState(0);
  const [valueReport, setValueReport] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const [limit] = useState(50);

  useEffect(() => {
    getValueReport().then(data => {
      setAllCategories(data?.items_by_category?.map(cat => cat.category_name) || []);
    }).catch(err => console.error('Failed to fetch categories:', err));
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, searchTerm, categoryFilter]);

  const fetchData = async () => {
    try {
      const skip = page * limit;
      const [summaryResult, reportData] = await Promise.all([
        getSummary(skip, limit, searchTerm || null, categoryFilter || null),
        getValueReport(searchTerm || null, categoryFilter || null)
      ]);
      setSummary(summaryResult.data || []);
      setTotal(summaryResult.total || 0);
      setValueReport(reportData);
    } catch (err) {
      console.error('Failed to fetch inventory data:', err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };


  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Inventory Summary</h2>
        <div>
          <button className="btn btn-success me-2" onClick={() => navigate('/inventory/add')}>
            <i className="bi bi-plus-circle me-2"></i>
            Add Inventory
          </button>
          <button className="btn btn-danger me-2" onClick={() => navigate('/inventory/remove')}>
            <i className="bi bi-dash-circle me-2"></i>
            Remove Inventory
          </button>
          <button className="btn btn-outline-secondary" onClick={fetchData}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh
          </button>
        </div>
      </div>


      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th className="text-end">Total Quantity</th>
                    <th className="text-end">Avg Unit Price</th>
                    <th className="text-end">Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No inventory items found
                      </td>
                    </tr>
                  ) : (
                    summary.map((item, index) => (
                      <tr key={index}>
                        <td><strong>{item.item_name}</strong></td>
                        <td>
                          <span className="badge bg-info">{item.category_name}</span>
                        </td>
                        <td className="small text-muted">{item.unit_of_measure}</td>
                        <td className="text-end">
                          <strong>{item.total_quantity}</strong>
                        </td>
                        <td className="text-end small">
                          {formatCurrency(item.average_unit_price)}
                        </td>
                        <td className="text-end">
                          <strong>{formatCurrency(item.total_value)}</strong>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {summary.length > 0 && (
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan="5" className="text-end fw-bold">Page Total:</td>
                      <td className="text-end fw-bold">
                        {formatCurrency(
                          summary.reduce((sum, item) => sum + parseFloat(item.total_value), 0)
                        )}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} items
              </div>
              <div>
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {valueReport?.items_by_category && valueReport.items_by_category.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="card-title mb-0">Value by Category</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="text-end">Items</th>
                    <th className="text-end">Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {valueReport.items_by_category.map((cat, index) => (
                    <tr key={index}>
                      <td><strong>{cat.category_name}</strong></td>
                      <td className="text-end">{cat.item_count}</td>
                      <td className="text-end">{formatCurrency(cat.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-secondary">
                  <tr>
                    <td className="text-end fw-bold">Total:</td>
                    <td className="text-end fw-bold">
                      {valueReport.items_by_category.reduce((sum, cat) => sum + cat.item_count, 0)}
                    </td>
                    <td className="text-end fw-bold">
                      {formatCurrency(
                        valueReport.items_by_category.reduce((sum, cat) => sum + parseFloat(cat.total_value), 0)
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySummaryPage;
